import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRequeueCampaignOutcomesMutation } from "@/store/rest_api";
import type { CampaignContactStatus } from "@/model/interface/CampaignContact";
import { apiErrorMessage } from "@/util/apiError";
import { calendarDateIn } from "@/util/date";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REQUEUE_OUTCOMES: { status: CampaignContactStatus; label: string }[] = [
  { status: "NO_ANSWER", label: "No answer" },
  { status: "VOICEMAIL", label: "Voicemail" },
];

interface RequeueOutcomesDialogProps {
  campaignId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequeueOutcomesDialog({ campaignId, open, onOpenChange }: RequeueOutcomesDialogProps) {
  const [requeueOutcomes, { isLoading }] = useRequeueCampaignOutcomesMutation();
  const [statuses, setStatuses] = useState<CampaignContactStatus[]>(["NO_ANSWER", "VOICEMAIL"]);
  const [before, setBefore] = useState(calendarDateIn());

  const toggleStatus = (status: CampaignContactStatus, checked: boolean) => {
    setStatuses((current) => (checked ? [...current, status] : current.filter((existing) => existing !== status)));
  };

  const handleRequeue = async () => {
    try {
      const requeued = await requeueOutcomes({ campaign: { id: campaignId }, requeue: { statuses, before } }).unwrap();
      toast.success(requeued === 1 ? "1 lead is back in the queue" : `${requeued} leads are back in the queue`);
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not requeue the leads"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Requeue leads</DialogTitle>
          <DialogDescription>Put finished leads back in the queue to call them again.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Last outcome</p>
            {REQUEUE_OUTCOMES.map(({ status, label }) => (
              <div key={status} className="flex items-center gap-2">
                <Checkbox
                  id={`requeue-${status}`}
                  checked={statuses.includes(status)}
                  onCheckedChange={(checked) => toggleStatus(status, checked === true)}
                />
                <Label htmlFor={`requeue-${status}`} className="text-sm font-normal">
                  {label}
                </Label>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="requeue-before">Ended before</Label>
            <Input id="requeue-before" type="date" value={before} onChange={(event) => setBefore(event.target.value)} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={() => void handleRequeue()} disabled={statuses.length === 0 || !before || isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Requeue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
